import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, CreditCard, TrendingDown, ShieldCheck, BarChart3, Layers, Zap, DollarSign } from 'lucide-react';

const MerchantProcessing = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950/40" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-10 right-10 w-[500px] h-[500px] bg-emerald-500/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 left-0 w-[350px] h-[350px] bg-slate-500/10 rounded-full blur-[100px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
                <CreditCard className="h-4 w-4 text-emerald-300" />
                <span className="text-xs font-semibold text-emerald-200 uppercase tracking-wider">Merchant Processing</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Lower Your Costs,<br />
                <span className="text-emerald-300">Strengthen Your Margins</span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-md">
                Credit card processing is one of the largest hidden expenses in your business. We reduce those fees and put that money back into your bottom line.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="merchant-hero-contact" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-sm hover:bg-slate-100 transition-all">
                  Request a Fee Analysis <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/apply" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-sm hover:bg-white/10 transition-all">
                  Get Started
                </Link>
              </div>
            </div>

            {/* Floating value props */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {[
                { icon: <TrendingDown className="h-6 w-6" />, value: 'Lower Fees', label: 'Reduce processing costs', color: 'text-emerald-300' },
                { icon: <ShieldCheck className="h-6 w-6" />, value: 'Bank-Grade', label: 'Enterprise infrastructure', color: 'text-blue-300' },
                { icon: <BarChart3 className="h-6 w-6" />, value: 'Scalable', label: 'Built for growth', color: 'text-amber-300' },
                { icon: <Layers className="h-6 w-6" />, value: 'Multi Channel', label: 'In store, online, mobile', color: 'text-purple-300' },
              ].map((item, i) => (
                <div
                  key={item.label}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className={`${item.color} mb-3`}>{item.icon}</div>
                  <div className="text-lg font-bold text-white mb-0.5" style={{ fontFamily: 'var(--font-heading)' }}>{item.value}</div>
                  <div className="text-xs text-slate-400 font-medium">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-700 mb-4 block">The Hidden Cost</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                You're Probably Overpaying, Most Businesses Are
              </h2>
              <p className="text-slate-500 leading-relaxed mb-6">
                Credit card processing fees are complex by design. Between interchange markups, hidden surcharges, and opaque pricing, most businesses leave thousands of dollars on the table every year without realizing it.
              </p>
              <p className="text-slate-500 leading-relaxed mb-8">
                We analyze your current merchant statements, identify where you're overpaying, and implement a pricing structure that directly improves your cash flow. It's one of the simplest ways to increase profitability without changing a thing about how you operate.
              </p>
              <div className="flex gap-8">
                <div>
                  <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>2 to 4%</div>
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mt-1">Avg. Processing Cost</div>
                </div>
                <div className="w-px bg-slate-200" />
                <div>
                  <div className="text-3xl font-bold text-emerald-700" style={{ fontFamily: 'var(--font-heading)' }}>20 to 40%</div>
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mt-1">Typical Fee Reduction</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 md:p-10">
              <h3 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Common Issues We Find
              </h3>
              <div className="space-y-5">
                {[
                  { title: 'Inflated interchange markups', desc: 'Your processor is adding margin on top of margin.' },
                  { title: 'Bundled pricing that hides true costs', desc: 'Flat rate models like Stripe and Square simplify billing but often at a premium.' },
                  { title: 'Unnecessary monthly fees', desc: 'PCI compliance fees, statement fees, and batch fees that add up quietly.' },
                  { title: 'Account instability', desc: 'Aggregated processors can freeze funds or shut down accounts with little warning.' },
                ].map((issue) => (
                  <div key={issue.title} className="flex gap-4">
                    <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 mt-2" />
                    <div>
                      <div className="text-sm font-bold text-slate-900">{issue.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{issue.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Infrastructure */}
      <section className="py-24 md:py-32 bg-slate-900 text-white relative noise-overlay overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400 mb-4 block">Infrastructure</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Enterprise-Grade Processing
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Your payments are processed through CardConnect, a Fiserv company, the same infrastructure trusted by major financial institutions nationwide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <ShieldCheck className="h-7 w-7 text-emerald-400 mb-5" />
              <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Properly Underwritten</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Unlike aggregated services, your merchant account is individually underwritten. That means no sudden freezes, no unexpected holds, and no account shutdowns.
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <BarChart3 className="h-7 w-7 text-blue-400 mb-5" />
              <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Built for Volume</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                As your business grows, your processing scales with you. Custom pricing for higher volumes, with infrastructure that handles enterprise-level transaction loads.
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <Layers className="h-7 w-7 text-purple-400 mb-5" />
              <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Multi Channel Support</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Accept payments in person with terminals, online through e commerce, via digital invoicing, and on mobile, all through a single, unified merchant account.
              </p>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-xs text-slate-500">Powered by CardConnect, a Fiserv company</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-700 mb-3 block">Process</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Simple Onboarding, Immediate Savings
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Send Your Statement', desc: 'Upload a recent merchant processing statement. That\'s all we need to start.', icon: <Zap className="h-6 w-6" /> },
              { step: '02', title: 'We Analyze Your Fees', desc: 'Our team breaks down every line item and identifies exactly where you\'re overpaying.', icon: <BarChart3 className="h-6 w-6" /> },
              { step: '03', title: 'Review Your Savings', desc: 'We present a clear comparison showing your current costs vs. what we can offer.', icon: <DollarSign className="h-6 w-6" /> },
              { step: '04', title: 'Switch & Save', desc: 'We handle the transition. Start processing at lower rates with zero downtime.', icon: <CheckCircle className="h-6 w-6" /> },
            ].map((s, i) => (
              <div key={s.step} className="text-center relative">
                <div className="w-16 h-16 rounded-2xl bg-slate-900 text-white flex items-center justify-center mx-auto mb-6">
                  {s.icon}
                </div>
                <div className="text-xs font-bold text-emerald-700 uppercase tracking-widest mb-2">Step {s.step}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+48px)] w-[calc(100%-96px)]">
                    <div className="h-px bg-slate-200 w-full" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financing Complement */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 bento-card bg-white p-10 md:p-12">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-700 mb-4 block">Strategic Advantage</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Processing + Financing = Stronger Financial Performance
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">
                When you reduce your processing overhead and pair it with the right financing solution, you create real leverage. Lower operating costs mean better cash flow, stronger debt service coverage, and a healthier business profile, which can also improve your borrowing position.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  'Reduce costs without changing operations',
                  'Improve cash flow and profit margins',
                  'Strengthen your borrowing profile',
                  'One partner for financing and payments',
                ].map((b) => (
                  <div key={b} className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-600">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col gap-6">
              <div className="bg-slate-900 text-white rounded-2xl p-8 flex-1">
                <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Why Not Stripe or Square?</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">
                  Aggregated processors are convenient for startups, but as your business grows, their one size fits all pricing becomes expensive. Worse, they can freeze funds or close your account without warning because you don't have your own merchant account.
                </p>
                <p className="text-sm text-slate-400 leading-relaxed">
                  With a properly underwritten account, you get stability, transparency, and pricing that actually scales with your business.
                </p>
              </div>

              <Link
                to="/contact"
                data-testid="merchant-analysis-cta"
                className="bg-emerald-600 text-white rounded-2xl p-8 flex items-center justify-between group hover:bg-emerald-500 transition-colors"
              >
                <div>
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Get a Free Fee Analysis</h3>
                  <p className="text-sm text-emerald-200">See how much you could save</p>
                </div>
                <ArrowRight className="h-6 w-6 text-white group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-slate-950 to-emerald-950/40 text-white relative noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Stop Leaving Money<br />on the Table
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">
            Send us a recent processing statement. We'll show you exactly what you can save, no obligation, no pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="merchant-final-contact" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-8 py-4 rounded-sm hover:bg-slate-100 transition-colors">
              Request a Fee Analysis <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/apply" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-sm hover:bg-white/10 transition-colors">
              Apply for Financing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MerchantProcessing;
